/**
 * Firestore write helpers for user profile fields (tier, hints, marketing, login).
 */
// @ts-check
import { doc, runTransaction, serverTimestamp } from '@/lib/firebase';
import {
  arrayRemove,
  arrayUnion,
  getDoc,
  increment,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { VALID_ONBOARDING_HINTS } from '@/constants/onboardingHints';
import { MARKETING_OPT_IN_TEXT_VERSION } from '@/constants/marketingOptIn';
import {
  DEV_TIER_CHANGED_EVENT,
  USERS_COLLECTION,
  VALID_TIERS,
  VALID_WALL_TYPES,
  devTierStorageKey,
  isDevTierLocalOverrideEnabled,
} from '@/services/userServiceConstants';

/** @typedef {'free' | 'lite' | 'pro'} UserTier */

/**
 * Schrijft tier naar het user-profiel (handmatig/dev; geen billing-UI in deze slice).
 * @param {string} userId
 * @param {UserTier} tier
 */
export async function setUserTier(userId, tier) {
  if (!VALID_TIERS.has(tier)) {
    throw new Error(`Invalid tier: ${tier}`);
  }

  if (isDevTierLocalOverrideEnabled()) {
    localStorage.setItem(devTierStorageKey(userId), tier);
    window.dispatchEvent(
      new CustomEvent(DEV_TIER_CHANGED_EVENT, { detail: { userId, tier } }),
    );
    return;
  }

  const userRef = doc(db, USERS_COLLECTION, userId);
  await runTransaction(db, async (transaction) => {
    const userDoc = await transaction.get(userRef);
    if (userDoc.exists()) {
      transaction.update(userRef, { tier });
    } else {
      transaction.set(userRef, {
        tier,
        createdAt: serverTimestamp(),
      });
    }
  });
}

/**
 * Markeert een onboarding-hint als gezien (dedupe via arrayUnion).
 * @param {string} userId
 * @param {string} hint
 */
export async function markHintSeen(userId, hint) {
  if (!userId) {
    throw new Error('userId is required');
  }
  if (!VALID_ONBOARDING_HINTS.has(hint)) {
    throw new Error(`Invalid hint: ${hint}`);
  }

  const userRef = doc(db, USERS_COLLECTION, userId);
  await setDoc(userRef, { seenHints: arrayUnion(hint) }, { merge: true });
}

/**
 * Verwijdert een hint uit seenHints (bv. intro opnieuw starten).
 * @param {string} userId
 * @param {string} hint
 */
export async function unmarkHintSeen(userId, hint) {
  if (!userId) {
    throw new Error('userId is required');
  }
  if (!VALID_ONBOARDING_HINTS.has(hint)) {
    throw new Error(`Invalid hint: ${hint}`);
  }

  const userRef = doc(db, USERS_COLLECTION, userId);
  await setDoc(userRef, { seenHints: arrayRemove(hint) }, { merge: true });
}

export async function registerWallInterest(userId, wallType) {
  if (!userId) {
    throw new Error('userId is required');
  }
  if (!VALID_WALL_TYPES.has(wallType)) {
    throw new Error(`Invalid wallType: ${wallType}`);
  }

  const userRef = doc(db, USERS_COLLECTION, userId);
  await runTransaction(db, async (transaction) => {
    const userDoc = await transaction.get(userRef);
    if (userDoc.exists()) {
      transaction.update(userRef, {
        wallInterest: arrayUnion(wallType),
      });
    } else {
      transaction.set(userRef, {
        wallInterest: [wallType],
        createdAt: serverTimestamp(),
      });
    }
  });
}

/**
 * @param {Record<string, unknown>} [data]
 * @returns {boolean}
 */
export function isMarketingOptInGranted(data) {
  return data?.marketingOptIn === true;
}

/**
 * @param {string} userId
 * @param {{ optedIn: boolean; source: string }} options
 */
export async function setUserMarketingOptIn(userId, { optedIn, source }) {
  if (!userId) {
    throw new Error('userId is required');
  }
  if (typeof optedIn !== 'boolean') {
    throw new Error('optedIn must be a boolean');
  }
  if (typeof source !== 'string' || !source.trim()) {
    throw new Error('source is required');
  }

  const userRef = doc(db, USERS_COLLECTION, userId);
  await setDoc(
    userRef,
    {
      marketingOptIn: optedIn,
      marketingOptInAt: serverTimestamp(),
      marketingOptInSource: source,
      marketingOptInTextVersion: MARKETING_OPT_IN_TEXT_VERSION,
    },
    { merge: true },
  );
}

export async function setUserProfileDisplayName(userId, displayName) {
  if (!userId) {
    throw new Error('userId is required');
  }
  const trimmed = displayName.trim();
  if (!trimmed || trimmed.length > 50) {
    throw new Error('Invalid display name');
  }

  const userRef = doc(db, USERS_COLLECTION, userId);
  await setDoc(userRef, { displayName: trimmed }, { merge: true });
}

export async function incrementLoginCount(userId) {
  const userRef = doc(db, USERS_COLLECTION, userId);
  const snap = await getDoc(userRef);
  if (snap.exists()) {
    await updateDoc(userRef, {
      loginCount: increment(1),
      lastLoginAt: serverTimestamp(),
    });
  } else {
    await setDoc(userRef, {
      loginCount: 1,
      createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
    });
  }
}
